import { CheckCircle2, FileJson2, ListChecks, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { usePromptInitialInputs } from "../hooks/usePromptInitialInputs";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EvaluationPresetType } from "@/queries/smart-tables";
import { PromptVersion } from "@/types/apiGetters";

export interface AssertionPresetSubmitPayload {
  presetType: EvaluationPresetType;
  initialInputs: Record<string, string>;
  expectedValue?: string;
  assertions: string[];
}

export interface AssertionPresetSetupProps {
  promptVersion?: PromptVersion;
  isSubmitting: boolean;
  onSubmit: (payload: AssertionPresetSubmitPayload) => void;
}

export interface AssertionPresetContent {
  groundingLabel?: string;
  groundingHint?: string;
  groundingPlaceholder?: string;
  assertionTitle: string;
  assertionHint: string;
  assertionPlaceholder: string;
  defaultAssertions: string[];
  resultLabel: string;
  missingText: string;
  tableInputText: string;
}

interface AssertionDraft {
  id: string;
  value: string;
}

interface AssertionPresetSetupBaseProps extends AssertionPresetSetupProps {
  presetType: EvaluationPresetType;
  content: AssertionPresetContent;
}

const makeAssertionId = () => `assertion-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const assertionDraftsFromDefaults = (assertions: string[]): AssertionDraft[] =>
  assertions.map(value => ({ id: makeAssertionId(), value }));

const normalizedAssertions = (assertions: AssertionDraft[]) =>
  assertions.map(assertion => assertion.value.replace(/\s+/g, " ").trim()).filter(Boolean);

const AssertionPresetSetupBase = ({
  presetType,
  content,
  promptVersion,
  isSubmitting,
  onSubmit,
}: AssertionPresetSetupBaseProps) => {
  const { inputVariables, initialInputs, setInitialInputs } = usePromptInitialInputs(promptVersion);
  const [expectedValue, setExpectedValue] = useState("");
  const [assertions, setAssertions] = useState<AssertionDraft[]>(() =>
    assertionDraftsFromDefaults(content.defaultAssertions),
  );

  useEffect(() => {
    setExpectedValue("");
    setAssertions(assertionDraftsFromDefaults(content.defaultAssertions));
  }, [content.defaultAssertions]);

  const readyAssertions = normalizedAssertions(assertions);
  const requiresGrounding = Boolean(content.groundingLabel);
  const canCreate =
    (!requiresGrounding || expectedValue.trim().length > 0) && readyAssertions.length > 0;

  const updateAssertion = (id: string, value: string) => {
    setAssertions(previous =>
      previous.map(assertion => (assertion.id === id ? { ...assertion, value } : assertion)),
    );
  };

  const addAssertion = () => {
    setAssertions(previous => [...previous, { id: makeAssertionId(), value: "" }]);
  };

  const removeAssertion = (id: string) => {
    setAssertions(previous =>
      previous.length === 1
        ? [{ ...previous[0], value: "" }]
        : previous.filter(assertion => assertion.id !== id),
    );
  };

  return (
    <div className="flex min-h-0 flex-col">
      <div className="max-h-[58vh] overflow-y-auto px-4 py-4 sm:px-5">
        {inputVariables.length > 0 && (
          <div className="border-b border-gray-100 pb-4">
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-950">Try one example row</p>
              <span className="text-xs text-gray-400">Optional</span>
            </div>
            <div className="space-y-1.5">
              {inputVariables.map(variable => (
                <div
                  key={variable}
                  className="grid gap-1.5 sm:grid-cols-[120px_minmax(0,1fr)] sm:items-center sm:gap-2"
                >
                  <label
                    className="truncate text-xs text-gray-500"
                    htmlFor={`${presetType.toLowerCase()}-input-${variable}`}
                  >
                    {variable}
                  </label>
                  <Input
                    id={`${presetType.toLowerCase()}-input-${variable}`}
                    className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
                    value={initialInputs[variable] ?? ""}
                    onChange={event =>
                      setInitialInputs(previous => ({
                        ...previous,
                        [variable]: event.target.value,
                      }))
                    }
                    placeholder="Value"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {requiresGrounding && (
          <div className="space-y-2 border-b border-gray-100 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <label
                  className="text-sm font-medium text-gray-950"
                  htmlFor={`${presetType}-expected`}
                >
                  {content.groundingLabel}
                </label>
                <p className="mt-1 text-xs leading-5 text-gray-500">{content.groundingHint}</p>
              </div>
              <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
                Required
              </span>
            </div>
            <Textarea
              id={`${presetType}-expected`}
              value={expectedValue}
              onChange={event => setExpectedValue(event.target.value)}
              placeholder={content.groundingPlaceholder}
              className="min-h-[112px] resize-none border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
            />
          </div>
        )}

        <div className="space-y-2 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-950">{content.assertionTitle}</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">{content.assertionHint}</p>
            </div>
            <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
              {readyAssertions.length || 0} checks
            </span>
          </div>
          <div className="space-y-2">
            {assertions.map((assertion, index) => (
              <div
                key={assertion.id}
                className="flex gap-2 rounded-lg border border-gray-200 bg-white p-2"
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-gray-50 text-xs font-medium text-gray-500 ring-1 ring-gray-100">
                  {index + 1}
                </div>
                <Textarea
                  value={assertion.value}
                  onChange={event => updateAssertion(assertion.id, event.target.value)}
                  placeholder={content.assertionPlaceholder}
                  className="min-h-[58px] flex-1 resize-none border-0 bg-transparent px-0 py-0 text-sm shadow-none focus-visible:ring-0"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-900"
                  onClick={() => removeAssertion(assertion.id)}
                  aria-label="Remove assertion"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addAssertion}
            className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 text-sm text-gray-500 transition hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800"
          >
            <Plus className="h-3.5 w-3.5" />
            Add assertion
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">
            Table this creates
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-2.5">
              <ListChecks className="mb-2 h-4 w-4 text-gray-500" />
              <p className="text-xs font-medium text-gray-900">Inputs</p>
              <p className="mt-0.5 text-xs leading-4 text-gray-500">{content.tableInputText}</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2.5">
              <FileJson2 className="mb-2 h-4 w-4 text-gray-500" />
              <p className="text-xs font-medium text-gray-900">Output</p>
              <p className="mt-0.5 text-xs leading-4 text-gray-500">
                The selected prompt version runs once.
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-2.5">
              <CheckCircle2 className="mb-2 h-4 w-4 text-gray-500" />
              <p className="text-xs font-medium text-gray-900">{content.resultLabel}</p>
              <p className="mt-0.5 text-xs leading-4 text-gray-500">
                One pass/fail result per assertion.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">
            {canCreate ? "Ready to create." : content.missingText}
          </p>
          <Button
            type="button"
            onClick={() =>
              onSubmit({
                presetType,
                initialInputs,
                expectedValue: requiresGrounding ? expectedValue.trim() : undefined,
                assertions: readyAssertions,
              })
            }
            disabled={isSubmitting || !canCreate}
          >
            {isSubmitting ? "Creating…" : "Create and run"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AssertionPresetSetupBase;
